import * as React from "react";
import PortalWrapper from "../../../../../ui/smart/portal-wrapper/portal-wrapper";
import { PreviewAsModalStyle } from "./preview-as-modal-style";
import ClickOutsideContainer from "../../../../../../sharedUI/ui/smart/click-outside-container/click-outside-container";
import GroupedConfigUIs from "../../../../../ui/smart/grouped-config-uis/grouped-config-uis";
import { PropertySection } from "common/generator/components/property-sections";
import { connect } from "react-redux";
import { RootState } from "../../../../../../store/types";
import { AppUserAuthenticator } from "common/components/types";
import { withUserProfiles } from "./user-profile-row-provider";
import { localStorageSetItem, getPreviewAsKey } from "webapp/lib/local-storage";
import classNames from "classnames";
import { isUserData, UserData } from "common/Database";
import { isEmptyOrUndefinedish, isValidEmailAddress } from "common/Support";
import Img from "webapp/ui/smart/img/img";
import AppIcon from "common/app-icon";
import { url as gravatar } from "gravatar";
import { UserProfileRow } from "common/components/user-profile";
import { UserPreviewIcon } from "sharedUI/ui/views/user-preview-icon/user-preview-icon";

interface Props {
    authenticator: AppUserAuthenticator;
    targetRef: React.RefObject<any>;
    onClose: () => void;
    userProfiles: UserProfileRow[];
    userData: UserData | undefined;
}

const PreviewAsModal: React.VFC<Props> = p => {
    const { authenticator, targetRef, onClose, userProfiles } = p;

    const [search, setSearch] = React.useState("");

    const userProfilesArea = React.useMemo(() => {
        let items = userProfiles;
        if (authenticator.virtualEmail !== undefined && !items.find(i => i.email === authenticator.virtualEmail)) {
            items = [
                {
                    key: "virtualEmail",
                    email: authenticator.virtualEmail,
                    imageURL: undefined,
                    name: authenticator.virtualEmail,
                    roles: [],
                },
                ...items,
            ];
        }

        const showSearch =
            isValidEmailAddress(search) &&
            items.find(profile => profile.email.toLowerCase() === search.toLowerCase()) === undefined;

        if (showSearch) {
            items = [
                {
                    key: "search",
                    email: search,
                    imageURL: undefined,
                    name: search,
                    roles: [],
                },
                ...items,
            ];
        }

        const profilesShowing = items.filter(
            profile =>
                search === "" ||
                profile.name?.includes(search) ||
                profile.email.includes(search) ||
                profile.roles.some(role => role.includes(search)) ||
                authenticator.virtualEmail === profile.email
        );

        const listItems = profilesShowing.map(profile => {
            const isSelected = authenticator.virtualEmail === profile.email;
            const name = isEmptyOrUndefinedish(profile.name) ? profile.email : profile.name ?? "";
            return (
                <li
                    key={profile.key}
                    className={classNames(isSelected && "selected")}
                    onClick={() => {
                        let emailToSet: string;
                        if (isSelected) {
                            emailToSet = "";
                        } else {
                            emailToSet = profile.email;
                        }
                        authenticator.setEmailAddressFromBuilder(emailToSet, true);
                        localStorageSetItem(getPreviewAsKey(authenticator.appID), emailToSet);
                    }}>
                    <div>
                        <Img
                            className="user-image"
                            src={
                                profile.imageURL ??
                                gravatar(profile.email, {
                                    default: "404",
                                })
                            }
                            alt=""
                            alternate={<UserPreviewIcon name={name} />}
                        />
                        <div className="profile-name">{name}</div>
                    </div>
                    {profile.roles.length > 0 && <div className="profile-roles">{profile.roles.join(", ")}</div>}
                    <AppIcon className="selected-icon" icon="01-33-check-1" size={14} />
                </li>
            );
        });

        return (
            <div className="user-profiles-area">
                <input
                    placeholder="Search by email, name, or role"
                    value={search}
                    autoFocus={true}
                    onChange={ev => setSearch(ev.target.value)}
                />
                <ul className="user-profile-list">{listItems}</ul>
            </div>
        );
    }, [authenticator, search, userProfiles]);

    return (
        <ClickOutsideContainer onClickOutside={onClose}>
            <PortalWrapper target={targetRef} onTargetClick={onClose}>
                <PreviewAsModalStyle className="click-outside-ignore">
                    <GroupedConfigUIs
                        variant={"tight"}
                        items={[
                            {
                                section: PropertySection.PreviewAs,
                                ui: userProfilesArea,
                            },
                        ]}
                    />
                </PreviewAsModalStyle>
            </PortalWrapper>
        </ClickOutsideContainer>
    );
};

const mapState = (state: RootState) => ({
    userData: isUserData(state.userData) ? state.userData : undefined,
});

const mapDispatch = {};

export default withUserProfiles(connect(mapState, mapDispatch)(PreviewAsModal));
